import { useCallback, useState } from "react";

import { AxiosError } from "axios";

import { useApiContext } from "../../../../api/context";
import { useCustomerId } from "../../../../Components/hooks/useCustomerId";
import useSegmentTrackEvent from "../../../../utils/useSegmentTrackEvent";
import { CLOUDZERO_FEATURE, CONNECTION_TEST_EVENT, createConnectionTestEventProperties } from "../segmenTracking";
import { type OnboardingRequest } from "./types";

export interface ConnectionResult {
  success: boolean;
  message: string;
  error?: {
    statusCode?: number;
    message: string;
  };
}

export const useTestConnection = () => {
  const customerId = useCustomerId();
  const api = useApiContext();
  const { trackEvent } = useSegmentTrackEvent();

  const trackConnectionTest = useCallback(
    (result: "success" | "failed", extraProps: Record<string, unknown> = {}) => {
      trackEvent(CONNECTION_TEST_EVENT, {
        ...createConnectionTestEventProperties(CLOUDZERO_FEATURE),
        source: "TestConnectionStep",
        connectionTestResult: result,
        ...extraProps,
      });
    },
    [trackEvent]
  );

  const testConnection = async (request: OnboardingRequest): Promise<ConnectionResult> => {
    try {
      await api.post(`/v1/customers/${customerId}/integrations/cloudzero/action/test-connection`, {
        fetchCosts: request.fetchCosts,
        fetchInsights: request.fetchInsights,
        apiKey: request.apiKey,
        orgName: request.orgName,
      });

      trackConnectionTest("success");
      return {
        success: true,
        message: "Your credentials are correct. Click on finish to onboard your organization.",
      };
    } catch (error) {
      trackConnectionTest("failed");

      if (error instanceof AxiosError && error.response?.status === 403) {
        return {
          success: false,
          message: "Invalid API key. Please check your API key permissions and try again.",
        };
      }

      return {
        success: false,
        message: "Something went wrong. Please try again.",
      };
    }
  };

  return {
    testConnection,
  };
};

export const useOnboardCloudZero = () => {
  const customerId = useCustomerId();
  const api = useApiContext();
  const [onboarding, setOnboarding] = useState(false);

  const onboard = async (request: OnboardingRequest): Promise<ConnectionResult> => {
    setOnboarding(true);
    try {
      await api.post(`/v1/customers/${customerId}/integrations/cloudzero/action/onboard`, request);

      return {
        success: true,
        message: "You have successfully connected your CloudZero account and we will start importing your data.",
      };
    } catch (error) {
      const apiError = error as AxiosError<{ error?: string }>;
      const statusCode = apiError.response?.status;

      if (statusCode === 409) {
        return {
          success: false,
          message: "Organization already onboarded",
        };
      }

      return {
        success: false,
        message: "Error during onboarding. Please try again.",
      };
    } finally {
      setOnboarding(false);
    }
  };

  return {
    onboard,
    onboarding,
  };
};
